/**
 * FRAMING RULES (B4, spec §D.11, §E.1, §E.4). Pure.
 *
 *   selectFraming(β, D)   bearing and pitch for a selected route:
 *                          t = clamp(log2(1 + D/4) / log2(36), 0, 1)
 *                          |β| ≤ 90°: bearing = clamp(β, ±40), pitch = 42 − 12·t
 *                          |β| > 90°: bearing = sign(β)·min(20, (180 − |β|)/2), pitch = 0.6·(42 − 12·t)
 *                          The map is never flipped: the route never runs toward the viewer.
 *   bloomFraming(b)       pitch 15, the current bearing clamped to ±40°
 *   chooseMove(…)         easeTo under 1.5 viewport diagonals and |Δzoom| < 1.5, else flyTo (curve 1.42)
 *   framePadding(…)       the camera padding for a selection: the published insets with the card-open
 *                          right inset from t = 0 (§A.6), or the card sheet on mobile
 */
import type { LngLat } from "@/contracts/data";
import type { Insets, Layout } from "@/contracts/store";
import { computeInsets, detectVariant } from "@/features/layout/layoutMath";
import { angleDelta } from "@/lib/geo";
import { TILE, toMerc } from "./fit";

const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);

/** Signed bearing in (−180, 180]. */
export const signedBearing = (deg: number): number => angleDelta(0, deg);

/** The §D.11 length term t (0 at D = 0, 1 at D ≥ 140 km). */
export const framingT = (D: number): number => clamp(Math.log2(1 + Math.max(0, D) / 4) / Math.log2(36), 0, 1);

export function selectFraming(beta: number, D: number): { bearing: number; pitch: number } {
  const b = signedBearing(beta);
  const base = 42 - 12 * framingT(D);
  if (Math.abs(b) <= 90) return { bearing: clamp(b, -40, 40), pitch: base };
  const sign = b < 0 ? -1 : 1;
  return { bearing: sign * Math.min(20, (180 - Math.abs(b)) / 2), pitch: 0.6 * base };
}

/** The §D.11 ceiling: pitch 42 at D = 0 (the formula's largest value). */
export const SELECT_PITCH_MAX = 42;

/**
 * The pitches a select fit may use when the §D.11 pitch cannot keep the site and the pin in the frame near
 * the region's edge (L1-01): down to 0.6 × the §D.11 pitch (the factor §D.11 already applies when the route
 * would run toward the viewer), and, for a route running into the screen, up to the §D.11 ceiling of 42°
 * (a steeper view foreshortens the far end, which is where the pin is). The bearing never changes.
 */
export function selectPitchRange(beta: number, D: number): [number, number] {
  const { pitch } = selectFraming(beta, D);
  const b = signedBearing(beta);
  return [0.6 * pitch, Math.abs(b) <= 90 ? Math.max(pitch, SELECT_PITCH_MAX) : pitch];
}

export const BLOOM_PITCH = 15;
export function bloomFraming(currentBearing: number): { bearing: number; pitch: number } {
  return { bearing: clamp(signedBearing(currentBearing), -40, 40), pitch: BLOOM_PITCH };
}

/** §E.1: easeTo for a short hop, flyTo (van Wijk, curve 1.42) for anything farther. */
export function chooseMove(from: { center: LngLat; zoom: number }, to: { center: LngLat; zoom: number }, width: number, height: number): "ease" | "fly" {
  const a = toMerc(from.center);
  const b = toMerc(to.center);
  const S = TILE * 2 ** from.zoom;
  const dist = Math.hypot((b[0] - a[0]) * S, (b[1] - a[1]) * S);
  const diag = Math.hypot(width, height);
  return dist < 1.5 * diag && Math.abs(to.zoom - from.zoom) < 1.5 ? "ease" : "fly";
}

/**
 * Camera padding while a card is open or opening (§A.6: "select() frames with the card-open right inset
 * from t = 0, because the card arrives before the camera settles"). Desktop/kiosk: the right inset grows
 * to G + Z + 12 + Wc + B (the left may give way on compact widths). Mobile: the sheet becomes the card at
 * its half snap. Whatever the store already publishes is kept when it is larger (safe areas).
 */
export function framePadding(o: { insets: Insets; width: number; height: number; layout: Layout; kiosk: boolean; card: boolean }): Insets {
  const i = o.insets;
  if (!o.card) return { ...i };
  const variant = o.layout === "kiosk" || o.kiosk ? "kiosk" : o.layout === "mobile" ? "mobile" : detectVariant(o.width, false);
  if (variant === "mobile") {
    const c = computeInsets({ variant, vw: o.width, vh: o.height, cardOpen: true, sheet: "half", sheetMode: "card" });
    return { ...i, bottom: Math.max(i.bottom, c.bottom) };
  }
  const c = computeInsets({ variant, vw: o.width, vh: o.height, cardOpen: true });
  return { ...i, right: Math.max(i.right, c.right), left: Math.min(i.left, c.left) };
}

/** The no-card padding (the drive: the card has left, the chrome is faded). */
export function driveFramePadding(o: { insets: Insets; width: number; height: number; layout: Layout; kiosk: boolean }): Insets {
  const i = o.insets;
  const variant = o.layout === "kiosk" || o.kiosk ? "kiosk" : o.layout === "mobile" ? "mobile" : detectVariant(o.width, false);
  if (variant === "mobile") return { ...i, bottom: Math.min(i.bottom, computeInsets({ variant, vw: o.width, vh: o.height, cardOpen: false, sheet: "peek" }).bottom) };
  const c = computeInsets({ variant, vw: o.width, vh: o.height, cardOpen: false });
  return { ...i, right: Math.min(i.right, c.right) };
}
